import { canonicalJson, sha256Hex } from '@trackeat/shared/hash';
import { openLocalDb } from '../db/local.js';
import {
  attachActiveOperatorAssertion,
  splitOperatorAssertion,
} from '../auth/activeOperatorSession.js';
import { withMasterCacheSerialization } from './stockCommandSqlite.js';

export type AtomicRpcKind =
  | 'production_plan_replace_v1'
  | 'production_day_transition_v1'
  | 'stock_reconciliation_v1'
  | 'food_supplier_match_archive_v1'
  | 'supplier_item_match_management_v1'
  | 'consultant_historical_lot_recount_v1';

export type AtomicRpcStatus = 'pending' | 'sending' | 'conflict';

export interface AtomicRpcConflict {
  code: string;
  reason: string;
  details?: unknown;
}

export interface AtomicRpcRecord<Request = Record<string, unknown>> {
  request_id: string;
  kind: AtomicRpcKind;
  tenant_id: string;
  business_id: string;
  scope_key: string;
  digest: string;
  request: Request;
  status: AtomicRpcStatus;
  attempts: number;
  last_error: string | null;
  conflict: AtomicRpcConflict | null;
  prerequisite_request_id?: string | null;
  queued_at: string;
  updated_at: string;
}

export interface AtomicRpcHandle {
  request_id: string;
  kind: AtomicRpcKind;
  tenant_id: string;
  scope_key: string;
  digest: string;
}

export interface AtomicRpcMasterEffect {
  table: string;
  id: string;
  op: 'insert' | 'update' | 'archive';
  base_version: number;
  patch: Record<string, unknown>;
}

export interface AtomicRpcLocalMaster {
  table: string;
  id: string;
  tenant_id: string | null;
  version: number;
  row: Record<string, unknown>;
  archived: boolean;
  updated_at: string;
}

export interface AtomicRpcEnqueueContext {
  listMasters(table: string): Promise<AtomicRpcLocalMaster[]>;
}

export interface PreparedAtomicRpcMutation<
  Request extends object,
> {
  request: Request & { request_id: string };
  optimisticMasters: readonly AtomicRpcMasterEffect[];
}

export interface EnqueueAtomicRpcInput<Request extends object> {
  kind: AtomicRpcKind;
  tenantId: string;
  businessId: string;
  scopeKey: string;
  request: Request & { request_id: string };
  optimisticMasters:
    | readonly AtomicRpcMasterEffect[]
    | ((
      context: AtomicRpcEnqueueContext,
    ) => Promise<
      | readonly AtomicRpcMasterEffect[]
      | PreparedAtomicRpcMutation<Request>
    >);
  /**
   * A close-day request may subsume earlier draft writes for the exact same
   * production_days row. Those legacy writes and the atomic request are then
   * replaced in one SQLite transaction: there is never a crash window where
   * both (or neither) own the server mutation.
   *
   * This is deliberately opt-in and exact-row scoped. Other atomic RPCs keep
   * the strict FIFO overlap rejection.
   */
  absorbLegacyMasterPredecessors?: readonly {
    table: string;
    id: string;
  }[];
  prerequisiteRequestId?: string | null;
  now?: string;
}

export interface AtomicOptimisticMasterState {
  existed: boolean;
  tenant_id: string | null;
  version: number | null;
  row_json: string | null;
  archived: number | null;
  updated_at: string | null;
}

export interface AtomicRpcMasterLayerRow extends AtomicOptimisticMasterState {
  request_id: string;
  effect_json: string;
}

interface AtomicRpcSqlRow {
  request_id: string;
  kind: AtomicRpcKind;
  tenant_id: string;
  business_id: string;
  scope_key: string;
  digest: string;
  request_json: string;
  prerequisite_request_id: string | null;
  status: AtomicRpcStatus;
  attempts: number;
  last_error: string | null;
  conflict_json: string | null;
  queued_at: string;
  updated_at: string;
}

interface SqlTask {
  statement: string;
  values?: unknown[];
}

interface AbsorbedLegacyMasterPredecessor {
  rowId: number;
  table: string;
  id: string;
  op: 'insert' | 'update' | 'archive';
  baseVersion: number;
}

function parseAbsorbableLegacyMaster(
  row: { id: number; payload: string },
): AbsorbedLegacyMasterPredecessor | null {
  let value: {
    table?: unknown;
    id?: unknown;
    op?: unknown;
    base_version?: unknown;
  };
  try {
    value = JSON.parse(row.payload) as typeof value;
  } catch {
    return null;
  }
  if (
    typeof value.table !== 'string'
    || typeof value.id !== 'string'
    || !['insert', 'update', 'archive'].includes(String(value.op))
    || !Number.isSafeInteger(value.base_version)
    || Number(value.base_version) < 0
  ) {
    return null;
  }
  return {
    rowId: Number(row.id),
    table: value.table,
    id: value.id,
    op: value.op as AbsorbedLegacyMasterPredecessor['op'],
    baseVersion: Number(value.base_version),
  };
}

function isPreparedAtomicRpcMutation<
  Request extends object,
>(
  value:
    | readonly AtomicRpcMasterEffect[]
    | PreparedAtomicRpcMutation<Request>,
): value is PreparedAtomicRpcMutation<Request> {
  return !Array.isArray(value);
}

function requireText(value: string, field: string): void {
  if (!value.trim()) throw new Error(`atomic_rpc_invalid_${field}`);
}

function parseRecord<Request = Record<string, unknown>>(
  row: AtomicRpcSqlRow,
): AtomicRpcRecord<Request> {
  return {
    request_id: row.request_id,
    kind: row.kind,
    tenant_id: row.tenant_id,
    business_id: row.business_id,
    scope_key: row.scope_key,
    digest: row.digest,
    request: JSON.parse(row.request_json) as Request,
    status: row.status,
    attempts: Number(row.attempts),
    last_error: row.last_error,
    conflict: row.conflict_json
      ? JSON.parse(row.conflict_json) as AtomicRpcConflict
      : null,
    prerequisite_request_id: row.prerequisite_request_id ?? null,
    queued_at: row.queued_at,
    updated_at: row.updated_at,
  };
}

function handleOf(record: AtomicRpcRecord): AtomicRpcHandle {
  return {
    request_id: record.request_id,
    kind: record.kind,
    tenant_id: record.tenant_id,
    scope_key: record.scope_key,
    digest: record.digest,
  };
}

function masterStateFromRow(row: {
  tenant_id?: string | null;
  version?: number | null;
  row?: string | null;
  archived?: number | null;
  updated_at?: string | null;
} | null): AtomicOptimisticMasterState {
  if (!row) {
    return {
      existed: false,
      tenant_id: null,
      version: null,
      row_json: null,
      archived: null,
      updated_at: null,
    };
  }
  return {
    existed: true,
    tenant_id: row.tenant_id ?? null,
    version: row.version == null ? null : Number(row.version),
    row_json: row.row ?? null,
    archived: row.archived == null ? null : Number(row.archived),
    updated_at: row.updated_at ?? null,
  };
}

export function applyAtomicRpcMasterEffect(
  base: AtomicOptimisticMasterState,
  effect: AtomicRpcMasterEffect,
  updatedAt: string,
): AtomicOptimisticMasterState {
  const previous = base.existed && base.row_json
    ? JSON.parse(base.row_json) as Record<string, unknown>
    : {};
  const row: Record<string, unknown> = {
    ...previous,
    ...effect.patch,
    id: effect.id,
  };
  if (effect.op === 'archive') {
    row.archived_at =
      (effect.patch.archived_at as string | undefined) ?? updatedAt;
  }
  const version = effect.op === 'insert'
    ? 0
    : Number(base.version ?? effect.base_version) + 1;
  row.version = version;
  return {
    existed: true,
    tenant_id:
      (row.tenant_id as string | null | undefined)
      ?? base.tenant_id
      ?? null,
    version,
    row_json: JSON.stringify(row),
    archived: row.archived_at ? 1 : 0,
    updated_at: updatedAt,
  };
}

function writeMasterStateTasks(
  table: string,
  id: string,
  state: AtomicOptimisticMasterState,
): SqlTask[] {
  if (!state.existed) {
    return [{
      statement:
        `DELETE FROM masters_local
          WHERE table_name = ?
            AND id = ?`,
      values: [table, id],
    }];
  }
  return [{
    statement:
      `INSERT INTO masters_local(
         table_name,id,tenant_id,version,row,archived,updated_at
       ) VALUES(?,?,?,?,?,?,?)
       ON CONFLICT(table_name,id) DO UPDATE SET
         tenant_id = excluded.tenant_id,
         version = excluded.version,
         row = excluded.row,
         archived = excluded.archived,
         updated_at = excluded.updated_at`,
    values: [
      table,
      id,
      state.tenant_id,
      Number(state.version ?? 0),
      state.row_json ?? '{}',
      Number(state.archived ?? 0),
      state.updated_at ?? new Date().toISOString(),
    ],
  }];
}

export async function atomicRpcRequestDigest(
  request: Record<string, unknown>,
): Promise<string> {
  return sha256Hex(canonicalJson(request));
}

export async function enqueueAtomicRpcV1<
  Request extends object & { request_id: string },
>(
  input: EnqueueAtomicRpcInput<Request>,
): Promise<{ handle: AtomicRpcHandle; outcome: 'queued' | 'already_queued' | 'already_acknowledged' }> {
  requireText(input.tenantId, 'tenant_id');
  requireText(input.businessId, 'business_id');
  requireText(input.scopeKey, 'scope_key');
  const now = input.now ?? new Date().toISOString();
  return withMasterCacheSerialization(async () => {
    const db = await openLocalDb(null);
    const enqueueContext: AtomicRpcEnqueueContext = {
      async listMasters(table) {
        requireText(table, 'master_table');
        const result = await db.query(
          `SELECT table_name,id,tenant_id,version,row,archived,updated_at
             FROM masters_local
            WHERE table_name = ?
            ORDER BY id`,
          [table],
        );
        return (result.values ?? []).map((value) => {
          const master = value as {
            table_name: string;
            id: string;
            tenant_id: string | null;
            version: number;
            row: string;
            archived: number;
            updated_at: string;
          };
          return {
            table: master.table_name,
            id: master.id,
            tenant_id: master.tenant_id,
            version: Number(master.version),
            row: JSON.parse(master.row) as Record<string, unknown>,
            archived: Number(master.archived) === 1,
            updated_at: master.updated_at,
          };
        });
      },
    };
    const prepared = typeof input.optimisticMasters === 'function'
      ? await input.optimisticMasters(enqueueContext)
      : input.optimisticMasters;
    let request = isPreparedAtomicRpcMutation(prepared)
      ? prepared.request
      : input.request;
    const optimisticMasters = isPreparedAtomicRpcMutation(prepared)
      ? prepared.optimisticMasters
      : prepared;
    const absorbedLegacyRows: AbsorbedLegacyMasterPredecessor[] = [];
    const absorbTargets = input.absorbLegacyMasterPredecessors ?? [];
    if (absorbTargets.length > 0) {
      const requestShape = request as Record<string, unknown>;
      if (
        input.kind !== 'production_day_transition_v1'
        || requestShape.transition !== 'close'
        || absorbTargets.length !== 1
        || absorbTargets[0]?.table !== 'production_days'
        || absorbTargets[0]?.id !== requestShape.production_day_id
      ) {
        throw new Error('atomic_rpc_legacy_absorption_not_allowed');
      }
      const target = absorbTargets[0];
      const targetEffect = optimisticMasters.find(
        (effect) => effect.table === target.table && effect.id === target.id,
      );
      if (!targetEffect) {
        throw new Error('atomic_rpc_legacy_absorption_effect_missing');
      }
      const legacyResult = await db.query(
        `SELECT id,payload
           FROM pending_ops
          WHERE kind = 'master'
          ORDER BY id`,
      );
      for (const value of legacyResult.values ?? []) {
        const row = value as { id: number; payload: string };
        const parsed = parseAbsorbableLegacyMaster(row);
        if (
          parsed
          && parsed.table === target.table
          && parsed.id === target.id
        ) {
          absorbedLegacyRows.push(parsed);
        }
      }
      if (absorbedLegacyRows.length > 0) {
        let resultingLocalVersion: number | null = null;
        for (const [index, predecessor] of absorbedLegacyRows.entries()) {
          if (predecessor.op === 'archive') {
            throw new Error('atomic_rpc_legacy_absorption_archive_forbidden');
          }
          if (
            index > 0
            && predecessor.baseVersion !== resultingLocalVersion
          ) {
            throw new Error('atomic_rpc_legacy_absorption_chain_conflict');
          }
          resultingLocalVersion = predecessor.op === 'insert'
            ? 0
            : predecessor.baseVersion + 1;
        }
        if (resultingLocalVersion !== targetEffect.base_version) {
          throw new Error('atomic_rpc_legacy_absorption_tail_conflict');
        }
        const first = absorbedLegacyRows[0];
        request = {
          ...request,
          // An insert uses expected_version=0 for a missing server row. An
          // update starts from the earliest predecessor's real server base,
          // not from the optimistic local version after all draft writes.
          expected_version:
            first.op === 'insert' ? 0 : first.baseVersion,
        };
      }
    }
    requireText(request.request_id, 'request_id');
    const requestScope = request as {
      tenant_id?: unknown;
      business_id?: unknown;
    };
    if (
      requestScope.tenant_id !== input.tenantId
      || requestScope.business_id !== input.businessId
    ) {
      throw new Error('atomic_rpc_request_scope_mismatch');
    }
    request = attachActiveOperatorAssertion(request);
    const digestRequest = splitOperatorAssertion(request).value;
    const digest = await atomicRpcRequestDigest(
      digestRequest as unknown as Record<string, unknown>,
    );
    const existingResult = await db.query(
      `SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
              request_json,prerequisite_request_id,status,attempts,last_error,conflict_json,
              queued_at,updated_at
         FROM pending_atomic_rpcs_v1
        WHERE request_id = ?`,
      [request.request_id],
    );
    const existingRow = existingResult.values?.[0] as AtomicRpcSqlRow | undefined;
    if (existingRow) {
      const existing = parseRecord(existingRow);
      if (existing.digest !== digest || existing.kind !== input.kind) {
        throw new Error('atomic_rpc_request_id_digest_conflict');
      }
      return {
        handle: handleOf(existing),
        outcome: 'already_queued' as const,
      };
    }

    const finalizedResult = await db.query(
      `SELECT kind,digest
         FROM atomic_rpc_finalizations_v1
        WHERE request_id = ?`,
      [request.request_id],
    );
    const finalized = finalizedResult.values?.[0] as {
      kind: AtomicRpcKind;
      digest: string;
    } | undefined;
    if (finalized) {
      if (finalized.digest !== digest || finalized.kind !== input.kind) {
        throw new Error('atomic_rpc_finalized_digest_conflict');
      }
      return {
        handle: {
          request_id: request.request_id,
          kind: input.kind,
          tenant_id: input.tenantId,
          scope_key: input.scopeKey,
          digest,
        },
        outcome: 'already_acknowledged' as const,
      };
    }

    const scopeResult = await db.query(
      `SELECT request_id,status
         FROM pending_atomic_rpcs_v1
        WHERE kind = ?
          AND tenant_id = ?
          AND scope_key = ?
        LIMIT 1`,
      [input.kind, input.tenantId, input.scopeKey],
    );
    const scopeRow = scopeResult.values?.[0] as {
      request_id: string;
      status: AtomicRpcStatus;
    } | undefined;
    if (scopeRow) {
      throw new Error(
        scopeRow.status === 'conflict'
          ? 'atomic_rpc_scope_conflict_requires_resolution'
          : 'atomic_rpc_scope_already_pending',
      );
    }

    const effectKeys = new Set<string>();
    for (const effect of optimisticMasters) {
      requireText(effect.table, 'effect_table');
      requireText(effect.id, 'effect_id');
      if (!Number.isInteger(effect.base_version) || effect.base_version < 0) {
        throw new Error('atomic_rpc_effect_base_version_invalid');
      }
      const key = `${effect.table}:${effect.id}`;
      if (effectKeys.has(key)) {
        throw new Error(`atomic_rpc_duplicate_effect:${key}`);
      }
      effectKeys.add(key);
    }

    let prerequisiteRequestId = input.prerequisiteRequestId ?? null;
    if (prerequisiteRequestId) {
      if (prerequisiteRequestId === request.request_id) {
        throw new Error('atomic_rpc_self_prerequisite');
      }
      const prerequisiteResult = await db.query(
        `SELECT tenant_id,status
           FROM pending_atomic_rpcs_v1
          WHERE request_id = ?
          LIMIT 1`,
        [prerequisiteRequestId],
      );
      const prerequisite = prerequisiteResult.values?.[0] as {
        tenant_id: string;
        status: AtomicRpcStatus;
      } | undefined;
      if (!prerequisite) {
        // It ACKed between scope discovery and this serialized enqueue.
        prerequisiteRequestId = null;
      } else if (prerequisite.tenant_id !== input.tenantId) {
        throw new Error('atomic_rpc_prerequisite_tenant_mismatch');
      } else if (prerequisite.status === 'conflict') {
        throw new Error('atomic_rpc_prerequisite_conflict');
      }
    }

    const tasks: SqlTask[] = [{
      statement:
        `INSERT INTO pending_atomic_rpcs_v1(
           request_id,kind,tenant_id,business_id,scope_key,digest,request_json,
           prerequisite_request_id,status,attempts,last_error,conflict_json,
           queued_at,updated_at
         ) VALUES(?,?,?,?,?,?,?,?,'pending',0,NULL,NULL,?,?)`,
      values: [
        request.request_id,
        input.kind,
        input.tenantId,
        input.businessId,
        input.scopeKey,
        digest,
        JSON.stringify(request),
        prerequisiteRequestId,
        now,
        now,
      ],
    }];
    for (const predecessor of absorbedLegacyRows) {
      tasks.push({
        statement: `DELETE FROM pending_ops WHERE id = ?`,
        values: [predecessor.rowId],
      });
    }

    for (const effect of optimisticMasters) {
      const olderLegacyRows = await db.query(
        `SELECT id,payload
           FROM pending_ops
          WHERE kind = 'master'
          ORDER BY id`,
      );
      const absorbedRowIds = new Set(
        absorbedLegacyRows.map((predecessor) => predecessor.rowId),
      );
      const olderLegacyOverlap = (olderLegacyRows.values ?? []).some((value) => {
        const legacyRow = value as { id?: number; payload?: string };
        if (
          legacyRow.id !== undefined
          && absorbedRowIds.has(Number(legacyRow.id))
        ) {
          return false;
        }
        const payload = legacyRow.payload;
        if (!payload) return true;
        try {
          const op = JSON.parse(payload) as { table?: unknown; id?: unknown };
          return op.table === effect.table && op.id === effect.id;
        } catch {
          return true;
        }
      });
      if (olderLegacyOverlap) {
        throw new Error(
          `atomic_rpc_older_legacy_master_overlap:${effect.table}:${effect.id}`,
        );
      }

      // A StockCommand already queued on the same row is older. Letting a
      // later plan/day transition jump in front of it would invert server FIFO.
      const stockLayer = await db.query(
        `SELECT command_id
           FROM pending_stock_command_master_snapshots
          WHERE table_name = ?
            AND id = ?
          LIMIT 1`,
        [effect.table, effect.id],
      );
      if ((stockLayer.values?.length ?? 0) > 0) {
        throw new Error(
          `atomic_rpc_older_stock_command_overlap:${effect.table}:${effect.id}`,
        );
      }
      const atomicLayer = await db.query(
        `SELECT snapshot.request_id
           FROM pending_atomic_rpc_master_snapshots_v1 AS snapshot
           JOIN pending_atomic_rpcs_v1 AS rpc
             ON rpc.request_id = snapshot.request_id
          WHERE snapshot.table_name = ?
            AND snapshot.id = ?
          LIMIT 1`,
        [effect.table, effect.id],
      );
      if ((atomicLayer.values?.length ?? 0) > 0) {
        throw new Error(
          `atomic_rpc_older_atomic_overlap:${effect.table}:${effect.id}`,
        );
      }

      const currentResult = await db.query(
        `SELECT tenant_id,version,row,archived,updated_at
           FROM masters_local
          WHERE table_name = ?
            AND id = ?`,
        [effect.table, effect.id],
      );
      const currentRaw = currentResult.values?.[0] as {
        tenant_id: string | null;
        version: number;
        row: string;
        archived: number;
        updated_at: string;
      } | undefined;
      const current = masterStateFromRow(currentRaw ?? null);
      if (effect.op === 'insert' && current.existed) {
        throw new Error(`atomic_rpc_insert_exists:${effect.table}:${effect.id}`);
      }
      if (effect.op !== 'insert' && !current.existed) {
        throw new Error(`atomic_rpc_update_missing:${effect.table}:${effect.id}`);
      }
      if (
        current.existed
        && Number(current.version) !== effect.base_version
      ) {
        throw new Error(
          `atomic_rpc_version_conflict:${effect.table}:${effect.id}`,
        );
      }
      const optimistic = applyAtomicRpcMasterEffect(current, effect, now);
      const predecessorWasAbsorbed = absorbedLegacyRows.some(
        (predecessor) => (
          predecessor.table === effect.table
          && predecessor.id === effect.id
        ),
      );
      // The local row before this atomic layer already contains draft bumps
      // whose legacy envelopes are being removed. It is therefore not a valid
      // authoritative rollback base. Persist an "unknown server base" sentinel
      // instead: a terminal conflict removes the optimistic row, after which
      // the mandatory successful pull can adopt even a lower real version.
      // On success, the exact digest-bound RPC receipt replaces it directly.
      const rollback = predecessorWasAbsorbed
        ? masterStateFromRow(null)
        : current;
      tasks.push({
        statement:
          `INSERT INTO pending_atomic_rpc_master_snapshots_v1(
             request_id,table_name,id,existed,tenant_id,version,row_json,
             archived,updated_at,effect_json
           ) VALUES(?,?,?,?,?,?,?,?,?,?)`,
        values: [
          request.request_id,
          effect.table,
          effect.id,
          rollback.existed ? 1 : 0,
          rollback.tenant_id,
          rollback.version,
          rollback.row_json,
          rollback.archived,
          rollback.updated_at,
          JSON.stringify(effect),
        ],
      });
      tasks.push(...writeMasterStateTasks(effect.table, effect.id, optimistic));
    }

    await db.executeTransaction(tasks, true);
    return {
      handle: {
        request_id: request.request_id,
        kind: input.kind,
        tenant_id: input.tenantId,
        scope_key: input.scopeKey,
        digest,
      },
      outcome: 'queued' as const,
    };
  });
}

export async function listPendingAtomicRpcsV1(
  tenantId: string,
  limit = 20,
  kind?: AtomicRpcKind,
  includeBlocked = false,
  excludedRequestIds: ReadonlySet<string> = new Set(),
): Promise<AtomicRpcRecord[]> {
  const db = await openLocalDb(null);
  const kindFilter = kind ? 'AND kind = ?' : '';
  const excludedIds = [...excludedRequestIds];
  const exclusionFilter = excludedIds.length > 0
    ? `AND request_id NOT IN (${excludedIds.map(() => '?').join(',')})`
    : '';
  const prerequisiteFilter = includeBlocked
    ? ''
    : `AND (
         prerequisite_request_id IS NULL
         OR NOT EXISTS (
           SELECT 1
             FROM pending_atomic_rpcs_v1 AS prerequisite
            WHERE prerequisite.request_id =
                  pending_atomic_rpcs_v1.prerequisite_request_id
              AND prerequisite.status IN ('pending','sending','conflict')
         )
       )`;
  const result = await db.query(
    `SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
            request_json,prerequisite_request_id,status,attempts,last_error,conflict_json,
            queued_at,updated_at
      FROM pending_atomic_rpcs_v1
      WHERE tenant_id = ?
        AND status IN ('pending','sending')
        AND (last_error IS NULL OR last_error NOT LIKE 'client_prepare_failed:%'
          OR julianday(updated_at) <= julianday(?))
        ${prerequisiteFilter}
        ${kindFilter}
        ${exclusionFilter}
      ORDER BY attempts,queued_at,request_id
      LIMIT ?`,
    kind
      ? [tenantId, new Date(Date.now() - 300_000).toISOString(), kind, ...excludedIds, limit]
      : [tenantId, new Date(Date.now() - 300_000).toISOString(), ...excludedIds, limit],
  );
  const records: AtomicRpcRecord[] = [];
  for (const value of result.values ?? []) {
    const row = value as AtomicRpcSqlRow;
    try {
      records.push(parseRecord(row));
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      await db.run(
        `UPDATE pending_atomic_rpcs_v1
            SET status = 'pending', attempts = attempts + 1,
                last_error = ?, updated_at = ?
          WHERE request_id = ?`,
        [
          `client_prepare_failed:${reason}`,
          new Date().toISOString(),
          row.request_id,
        ],
      );
    }
  }
  return records;
}

export async function countAtomicRpcsByKindV1(
  tenantId: string,
  kind: AtomicRpcKind,
): Promise<number> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT count(*) AS count
      FROM pending_atomic_rpcs_v1
      WHERE tenant_id = ?
        AND kind = ?
        AND status IN ('pending','sending')`,
    [tenantId, kind],
  );
  return Number(
    (result.values?.[0] as { count?: number } | undefined)?.count ?? 0,
  );
}

export async function countPendingAtomicRpcsV1(
  tenantId?: string,
): Promise<number> {
  const db = await openLocalDb(null);
  const result = await db.query(
    tenantId
      ? `SELECT count(*) AS count
           FROM pending_atomic_rpcs_v1
          WHERE tenant_id = ?`
      : `SELECT count(*) AS count FROM pending_atomic_rpcs_v1`,
    tenantId ? [tenantId] : [],
  );
  return Number(
    (result.values?.[0] as { count?: number } | undefined)?.count ?? 0,
  );
}

export async function latestAtomicRpcConflictV1(input: {
  tenantId: string;
  kind?: AtomicRpcKind;
  scopeKey?: string;
}): Promise<AtomicRpcRecord | null> {
  const db = await openLocalDb(null);
  const conditions = ['tenant_id = ?', `status = 'conflict'`];
  const values: unknown[] = [input.tenantId];
  if (input.kind) {
    conditions.push('kind = ?');
    values.push(input.kind);
  }
  if (input.scopeKey) {
    conditions.push('scope_key = ?');
    values.push(input.scopeKey);
  }
  const result = await db.query(
    `SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
            request_json,prerequisite_request_id,status,attempts,last_error,conflict_json,
            queued_at,updated_at
       FROM pending_atomic_rpcs_v1
      WHERE ${conditions.join(' AND ')}
      ORDER BY updated_at DESC,request_id DESC
      LIMIT 1`,
    values,
  );
  const row = result.values?.[0] as AtomicRpcSqlRow | undefined;
  return row ? parseRecord(row) : null;
}

export async function findProductionDayReopenForScopeV1(input: {
  tenantId: string;
  businessId: string;
  sheetDate: string;
}): Promise<AtomicRpcRecord | null> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
            request_json,prerequisite_request_id,status,attempts,last_error,
            conflict_json,queued_at,updated_at
       FROM pending_atomic_rpcs_v1
      WHERE tenant_id = ?
        AND business_id = ?
        AND kind = 'production_day_transition_v1'
        AND status IN ('pending','sending','conflict')
      ORDER BY queued_at,request_id`,
    [input.tenantId, input.businessId],
  );
  for (const value of result.values ?? []) {
    const record = parseRecord(value as AtomicRpcSqlRow);
    const request = record.request as {
      transition?: unknown;
      sheet_date?: unknown;
    };
    if (
      request.transition === 'reopen'
      && request.sheet_date === input.sheetDate
    ) {
      return record;
    }
  }
  return null;
}

export async function hasProductionPlanConflictForDayV1(input: {
  tenantId: string;
  businessId: string;
  serviceDate: string;
}): Promise<boolean> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT request_json
       FROM pending_atomic_rpcs_v1
      WHERE tenant_id = ?
        AND business_id = ?
        AND kind = 'production_plan_replace_v1'
        AND status = 'conflict'`,
    [input.tenantId, input.businessId],
  );
  return (result.values ?? []).some((value) => {
    try {
      const request = JSON.parse(
        (value as { request_json: string }).request_json,
      ) as { service_date?: unknown };
      return request.service_date === input.serviceDate;
    } catch {
      return true;
    }
  });
}

export async function listDirectAtomicRpcDependentsV1(
  prerequisiteRequestId: string,
): Promise<AtomicRpcRecord[]> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
            request_json,prerequisite_request_id,status,attempts,last_error,
            conflict_json,queued_at,updated_at
       FROM pending_atomic_rpcs_v1
      WHERE prerequisite_request_id = ?
        AND status IN ('pending','sending')
      ORDER BY queued_at,request_id`,
    [prerequisiteRequestId],
  );
  return (result.values ?? []).map((value) => (
    parseRecord(value as AtomicRpcSqlRow)
  ));
}

async function requireAtomicRpc(
  handle: AtomicRpcHandle,
): Promise<AtomicRpcRecord> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
            request_json,prerequisite_request_id,status,attempts,last_error,conflict_json,
            queued_at,updated_at
       FROM pending_atomic_rpcs_v1
      WHERE request_id = ?`,
    [handle.request_id],
  );
  const row = result.values?.[0] as AtomicRpcSqlRow | undefined;
  if (!row) throw new Error('atomic_rpc_not_found');
  const record = parseRecord(row);
  if (
    record.digest !== handle.digest
    || record.kind !== handle.kind
    || record.tenant_id !== handle.tenant_id
  ) {
    throw new Error('atomic_rpc_handle_mismatch');
  }
  return record;
}

export async function beginAtomicRpcSendV1(
  handle: AtomicRpcHandle,
  now = new Date().toISOString(),
): Promise<AtomicRpcRecord> {
  return withMasterCacheSerialization(async () => {
    const current = await requireAtomicRpc(handle);
    if (current.status === 'conflict') {
      throw new Error('atomic_rpc_conflict_not_sendable');
    }
    if (current.status === 'sending') return current;
    const db = await openLocalDb(null);
    await db.run(
      `UPDATE pending_atomic_rpcs_v1
          SET status = 'sending',
              attempts = attempts + 1,
              last_error = NULL,
              updated_at = ?
        WHERE request_id = ?
          AND digest = ?`,
      [now, handle.request_id, handle.digest],
    );
    return {
      ...current,
      status: 'sending',
      attempts: current.attempts + 1,
      last_error: null,
      updated_at: now,
    };
  });
}

export async function retryAtomicRpcV1(
  handle: AtomicRpcHandle,
  reason: string,
  now = new Date().toISOString(),
): Promise<void> {
  requireText(reason, 'retry_reason');
  await withMasterCacheSerialization(async () => {
    const current = await requireAtomicRpc(handle);
    if (current.status === 'conflict') return;
    const db = await openLocalDb(null);
    await db.run(
      `UPDATE pending_atomic_rpcs_v1
          SET status = 'pending',
              last_error = ?,
              updated_at = ?
        WHERE request_id = ?
          AND digest = ?`,
      [reason, now, handle.request_id, handle.digest],
    );
  });
}

export async function markAtomicRpcConflictV1(
  handle: AtomicRpcHandle,
  conflict: AtomicRpcConflict,
  now = new Date().toISOString(),
): Promise<void> {
  requireText(conflict.code, 'conflict_code');
  requireText(conflict.reason, 'conflict_reason');
  await withMasterCacheSerialization(async () => {
    const current = await requireAtomicRpc(handle);
    if (current.status === 'conflict') return;
    const db = await openLocalDb(null);
    const dependentsResult = await db.query(
      `SELECT dependent_id
         FROM atomic_rpc_dependents_v1
        WHERE request_id = ?
          AND dependent_kind = 'stock_command'
          AND status = 'pending'
        ORDER BY queued_at,dependent_id`,
      [handle.request_id],
    );
    const dependentCommandIds = new Set(
      (dependentsResult.values ?? []).map((value) => (
        (value as { dependent_id: string }).dependent_id
      )),
    );
    const snapshotsResult = await db.query(
      `SELECT table_name,id,existed,tenant_id,version,row_json,
              archived,updated_at
         FROM pending_atomic_rpc_master_snapshots_v1
        WHERE request_id = ?
        ORDER BY table_name,id`,
      [handle.request_id],
    );
    const tasks: SqlTask[] = [];
    const targetBases = new Map<string, {
      table_name: string;
      id: string;
      state: AtomicOptimisticMasterState;
    }>();
    for (const value of snapshotsResult.values ?? []) {
      const snapshot = value as {
        table_name: string;
        id: string;
        existed: number;
        tenant_id: string | null;
        version: number | null;
        row_json: string | null;
        archived: number | null;
        updated_at: string | null;
      };
      targetBases.set(`${snapshot.table_name}:${snapshot.id}`, {
        table_name: snapshot.table_name,
        id: snapshot.id,
        state: {
          existed: Number(snapshot.existed) === 1,
          tenant_id: snapshot.tenant_id,
          version: snapshot.version == null ? null : Number(snapshot.version),
          row_json: snapshot.row_json,
          archived: snapshot.archived == null
            ? null
            : Number(snapshot.archived),
          updated_at: snapshot.updated_at,
        },
      });
    }

    if (dependentCommandIds.size > 0) {
      const placeholders = [...dependentCommandIds].map(() => '?').join(',');
      const dependentLayers = await db.query(
        `SELECT snapshot.command_id,snapshot.table_name,snapshot.id,
                snapshot.existed,snapshot.tenant_id,snapshot.version,
                snapshot.row_json,snapshot.archived,snapshot.updated_at,
                command.queued_at
           FROM pending_stock_command_master_snapshots AS snapshot
           JOIN pending_stock_commands AS command
             ON command.command_id = snapshot.command_id
          WHERE snapshot.command_id IN (${placeholders})
          ORDER BY command.queued_at,command.rowid`,
        [...dependentCommandIds],
      );
      for (const value of dependentLayers.values ?? []) {
        const snapshot = value as {
          table_name: string;
          id: string;
          existed: number;
          tenant_id: string | null;
          version: number | null;
          row_json: string | null;
          archived: number | null;
          updated_at: string | null;
        };
        const key = `${snapshot.table_name}:${snapshot.id}`;
        if (targetBases.has(key)) continue;
        targetBases.set(key, {
          table_name: snapshot.table_name,
          id: snapshot.id,
          state: {
            existed: Number(snapshot.existed) === 1,
            tenant_id: snapshot.tenant_id,
            version: snapshot.version == null
              ? null
              : Number(snapshot.version),
            row_json: snapshot.row_json,
            archived: snapshot.archived == null
              ? null
              : Number(snapshot.archived),
            updated_at: snapshot.updated_at,
          },
        });
      }

      for (const commandId of dependentCommandIds) {
        const commandConflict: AtomicRpcConflict = {
          code: 'atomic_rpc_dependency_conflict',
          reason:
            `Prerequisito atomico ${handle.request_id} rifiutato: `
            + 'il comando magazzino dipendente richiede riconciliazione.',
          details: {
            atomic_request_id: handle.request_id,
            atomic_kind: handle.kind,
            atomic_conflict: conflict,
          },
        };
        tasks.push(
          {
            statement:
              `DELETE FROM events_local
                WHERE id IN (
                  SELECT event_id
                    FROM pending_stock_command_events
                   WHERE command_id = ?
                )
                  AND rev IS NULL`,
            values: [commandId],
          },
          {
            statement:
              `UPDATE pending_stock_commands
                  SET status = 'conflict',
                      last_error = ?,
                      conflict_json = ?,
                      updated_at = ?
                WHERE command_id = ?
                  AND status IN ('pending','sending')`,
            values: [
              commandConflict.reason,
              JSON.stringify(commandConflict),
              now,
              commandId,
            ],
          },
        );
      }
      tasks.push({
        statement:
          `UPDATE atomic_rpc_dependents_v1
              SET status = 'quarantined',
                  reason = ?,
                  updated_at = ?
            WHERE request_id = ?
              AND dependent_kind = 'stock_command'
              AND status = 'pending'`,
        values: [conflict.reason, now, handle.request_id],
      });
    }

    for (const target of targetBases.values()) {
      const allLayersResult = await db.query(
        `SELECT snapshot.command_id,snapshot.table_name,snapshot.id,
                command.master_effects_json,command.status,command.queued_at
           FROM pending_stock_command_master_snapshots AS snapshot
           JOIN pending_stock_commands AS command
             ON command.command_id = snapshot.command_id
          WHERE snapshot.table_name = ?
            AND snapshot.id = ?
          ORDER BY command.queued_at,command.rowid`,
        [target.table_name, target.id],
      );
      let state = target.state;
      for (const value of allLayersResult.values ?? []) {
        const layer = value as {
          command_id: string;
          master_effects_json: string;
          status: string;
        };
        if (
          dependentCommandIds.has(layer.command_id)
          || !['pending', 'sending'].includes(layer.status)
        ) {
          continue;
        }
        const effects = JSON.parse(
          layer.master_effects_json,
        ) as AtomicRpcMasterEffect[];
        const effect = effects.find((candidate) => (
          candidate.table === target.table_name
          && candidate.id === target.id
        ));
        if (!effect) {
          throw new Error(
            `atomic_rpc_stock_dependency_effect_missing:${layer.command_id}`,
          );
        }
        tasks.push({
          statement:
            `UPDATE pending_stock_command_master_snapshots
                SET existed = ?,tenant_id = ?,version = ?,row_json = ?,
                    archived = ?,updated_at = ?
              WHERE command_id = ?
                AND table_name = ?
                AND id = ?`,
          values: [
            state.existed ? 1 : 0,
            state.tenant_id,
            state.version,
            state.row_json,
            state.archived,
            state.updated_at,
            layer.command_id,
            target.table_name,
            target.id,
          ],
        });
        state = applyAtomicRpcMasterEffect(state, effect, now);
      }
      tasks.push(...writeMasterStateTasks(
        target.table_name,
        target.id,
        state,
      ));
    }
    tasks.push({
      statement:
        `UPDATE pending_atomic_rpcs_v1
            SET status = 'conflict',
                last_error = ?,
                conflict_json = ?,
                updated_at = ?
          WHERE request_id = ?
            AND digest = ?`,
      values: [
        conflict.reason,
        JSON.stringify(conflict),
        now,
        handle.request_id,
        handle.digest,
      ],
    });
    await db.executeTransaction(tasks, true);
  });
}

/**
 * Terminal prerequisite failure is a causal failure, not merely a transport
 * error. Quarantine direct atomic descendants and same-business/date day
 * transitions of a rejected plan before any dispatcher can send them.
 */
export async function markAtomicRpcConflictCascadeV1(
  handle: AtomicRpcHandle,
  conflict: AtomicRpcConflict,
  visited = new Set<string>(),
): Promise<void> {
  if (visited.has(handle.request_id)) return;
  visited.add(handle.request_id);
  const current = await requireAtomicRpc(handle);
  await markAtomicRpcConflictV1(handle, conflict);
  const dependents = new Map<string, AtomicRpcRecord>();
  for (const dependent of await listDirectAtomicRpcDependentsV1(
    current.request_id,
  )) {
    dependents.set(dependent.request_id, dependent);
  }
  if (current.kind === 'production_plan_replace_v1') {
    const planRequest = current.request as { service_date?: unknown };
    const dayCandidates = await listPendingAtomicRpcsV1(
      current.tenant_id,
      1000,
      'production_day_transition_v1',
      true,
    );
    for (const candidate of dayCandidates) {
      const dayRequest = candidate.request as { sheet_date?: unknown };
      if (
        candidate.business_id === current.business_id
        && typeof planRequest.service_date === 'string'
        && dayRequest.sheet_date === planRequest.service_date
      ) {
        dependents.set(candidate.request_id, candidate);
      }
    }
  }
  for (const dependent of dependents.values()) {
    const dependentConflict: AtomicRpcConflict = {
      code: 'atomic_rpc_prerequisite_conflict',
      reason:
        `Prerequisito ${current.kind} ${current.request_id} rifiutato: `
        + 'operazione dipendente ritirata e da riconciliare.',
      details: {
        prerequisite_request_id: current.request_id,
        prerequisite_kind: current.kind,
        prerequisite_conflict: conflict,
      },
    };
    await markAtomicRpcConflictCascadeV1(
      handleOf(dependent),
      dependentConflict,
      visited,
    );
  }
}

export async function acknowledgeAtomicRpcV1(
  handle: AtomicRpcHandle,
  now = new Date().toISOString(),
): Promise<void> {
  await withMasterCacheSerialization(async () => {
    const db = await openLocalDb(null);
    const currentResult = await db.query(
      `SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
              request_json,prerequisite_request_id,status,attempts,last_error,conflict_json,
              queued_at,updated_at
         FROM pending_atomic_rpcs_v1
        WHERE request_id = ?`,
      [handle.request_id],
    );
    const row = currentResult.values?.[0] as AtomicRpcSqlRow | undefined;
    if (!row) {
      const finalResult = await db.query(
        `SELECT kind,digest
           FROM atomic_rpc_finalizations_v1
          WHERE request_id = ?`,
        [handle.request_id],
      );
      const final = finalResult.values?.[0] as {
        kind: AtomicRpcKind;
        digest: string;
      } | undefined;
      if (
        !final
        || final.kind !== handle.kind
        || final.digest !== handle.digest
      ) {
        throw new Error('atomic_rpc_ack_missing');
      }
      return;
    }
    const current = parseRecord(row);
    if (
      current.digest !== handle.digest
      || current.kind !== handle.kind
      || current.status === 'conflict'
    ) {
      throw new Error('atomic_rpc_ack_state_mismatch');
    }
    await db.executeTransaction([
      {
        statement:
          `INSERT INTO atomic_rpc_finalizations_v1(
             request_id,kind,digest,outcome,reason,finalized_at
           ) VALUES(?,?,?,'acknowledged',NULL,?)
           ON CONFLICT(request_id) DO NOTHING`,
        values: [
          handle.request_id,
          handle.kind,
          handle.digest,
          now,
        ],
      },
      {
        statement:
          `DELETE FROM atomic_rpc_dependents_v1
            WHERE request_id = ?`,
        values: [handle.request_id],
      },
      {
        statement:
          `DELETE FROM pending_atomic_rpc_master_snapshots_v1
            WHERE request_id = ?`,
        values: [handle.request_id],
      },
      {
        statement:
          `DELETE FROM pending_atomic_rpcs_v1
            WHERE request_id = ?
              AND digest = ?`,
        values: [handle.request_id, handle.digest],
      },
    ], true);
  });
}

/**
 * Explicitly closes a terminal conflict after the caller has pulled/adopted
 * authoritative server truth. Optimistic layers were already rolled back when
 * the conflict was marked; this transaction only releases the scope lock and
 * leaves a durable resolution tombstone. A dependent StockCommand cannot be
 * meaningfully retried after its prerequisite was rejected: on this explicit,
 * post-pull resolution it is converted to a durable permanent-rejection
 * finalization and its inert optimistic envelope is removed. That finalization
 * is surfaced by the existing stock reconciliation UI across restarts.
 */
export async function resolveAtomicRpcConflictV1(
  handle: AtomicRpcHandle,
  reason: string,
  now = new Date().toISOString(),
): Promise<void> {
  requireText(reason, 'resolution_reason');
  await withMasterCacheSerialization(async () => {
    const current = await requireAtomicRpc(handle);
    if (current.status !== 'conflict') {
      throw new Error('atomic_rpc_resolution_requires_conflict');
    }
    const db = await openLocalDb(null);
    const dependentsResult = await db.query(
      `SELECT dependency.dependent_id
         FROM atomic_rpc_dependents_v1 AS dependency
        WHERE dependency.request_id = ?
          AND dependency.dependent_kind = 'stock_command'
          AND dependency.status = 'quarantined'
        ORDER BY dependency.queued_at,dependency.dependent_id`,
      [handle.request_id],
    );
    const dependentCommandIds = (dependentsResult.values ?? []).map(
      (value) => (value as { dependent_id: string }).dependent_id,
    );
    const blockedLegacyResult = await db.query(
      `SELECT id,payload
         FROM pending_ops
        WHERE kind = 'master'
        ORDER BY id`,
    );
    const blockedLegacyIds: number[] = [];
    for (const value of blockedLegacyResult.values ?? []) {
      const row = value as { id: number; payload: string };
      try {
        const payload = JSON.parse(row.payload) as Record<string, unknown>;
        if (
          payload.__trackeat_atomic_prerequisite_request_id
          === handle.request_id
        ) {
          blockedLegacyIds.push(Number(row.id));
        }
      } catch {
        // A corrupt legacy row is not silently removed by conflict resolution.
      }
    }
    const tasks: SqlTask[] = [
      {
        statement:
          `INSERT INTO atomic_rpc_finalizations_v1(
             request_id,kind,digest,outcome,reason,finalized_at
           ) VALUES(?,?,?,'resolved_conflict',?,?)
           ON CONFLICT(request_id) DO UPDATE SET
             outcome = excluded.outcome,
             reason = excluded.reason,
             finalized_at = excluded.finalized_at`,
        values: [
          handle.request_id,
          handle.kind,
          handle.digest,
          reason,
          now,
        ],
      },
    ];
    for (const commandId of dependentCommandIds) {
      const dependentReason =
        `Prerequisito ${handle.kind} ${handle.request_id} rifiutato; `
        + `riconciliazione adottata: ${reason}`;
      tasks.push(
        {
          statement:
            `INSERT INTO stock_command_finalizations(
               command_id,digest,outcome,reason,finalized_at
             )
             SELECT command_id,digest,'permanently_rejected',?,?
               FROM pending_stock_commands
              WHERE command_id = ?
                AND status = 'conflict'
             ON CONFLICT(command_id) DO UPDATE SET
               outcome = excluded.outcome,
               reason = excluded.reason,
               finalized_at = excluded.finalized_at`,
          values: [dependentReason, now, commandId],
        },
        {
          statement:
            `DELETE FROM events_local
              WHERE id IN (
                SELECT event_id
                  FROM pending_stock_command_events
                 WHERE command_id = ?
              )
                AND rev IS NULL`,
          values: [commandId],
        },
        {
          statement:
            `DELETE FROM pending_stock_command_events
              WHERE command_id = ?`,
          values: [commandId],
        },
        {
          statement:
            `DELETE FROM pending_stock_command_master_snapshots
              WHERE command_id = ?`,
          values: [commandId],
        },
        {
          statement:
            `DELETE FROM pending_stock_commands
              WHERE command_id = ?
                AND status = 'conflict'`,
          values: [commandId],
        },
      );
    }
    for (const pendingOpId of blockedLegacyIds) {
      // The mandatory pull already restored this production master from the
      // server. A write prepared only after an optimistic reopen cannot be
      // replayed when that reopen was rejected.
      tasks.push({
        statement: `DELETE FROM pending_ops WHERE id = ?`,
        values: [pendingOpId],
      });
    }
    tasks.push(
      {
        statement:
          `DELETE FROM atomic_rpc_dependents_v1
            WHERE request_id = ?`,
        values: [handle.request_id],
      },
      {
        statement:
          `DELETE FROM pending_atomic_rpc_master_snapshots_v1
            WHERE request_id = ?`,
        values: [handle.request_id],
      },
      {
        statement:
          `DELETE FROM pending_atomic_rpcs_v1
            WHERE request_id = ?
              AND digest = ?
              AND status = 'conflict'`,
        values: [handle.request_id, handle.digest],
      },
    );
    await db.executeTransaction(tasks, true);
  });
}

/**
 * The scope lock may be released only after this device has successfully
 * pulled authoritative server state. Keeping this orchestration next to the
 * outbox makes the offline and failed-pull behavior directly regression
 * testable instead of relying on a page-level convention.
 */
export async function resolveAtomicRpcConflictAfterPullV1(input: {
  handle: AtomicRpcHandle;
  online: boolean;
  pullAuthoritative: () => Promise<unknown>;
  reason: string;
  now?: string;
}): Promise<void> {
  if (!input.online) {
    throw new Error('atomic_rpc_conflict_resolution_requires_online');
  }
  await input.pullAuthoritative();
  await resolveAtomicRpcConflictV1(
    input.handle,
    input.reason,
    input.now,
  );
}
